import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormSection from './FormSection';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import { CityAutocomplete } from './CityAutocomplete';
import { CoordinateSelector } from './CoordinateSelector';
import { AvatarUpload } from './AvatarUpload';
import { FileUpload } from './FileUpload';
import {
  validateField,
  validateForm,
  type ValidationErrors,
  type BusinessProfileFormData,
  type ScheduleSlot,
} from '../../utils/businessProfileValidation';
import type { LegalForm, TPayCategory } from '../../types/businessProfile';
import type { City } from '../../services/searchService';

type DayType = 'weekdays' | 'saturday' | 'sunday';
type ScheduleType = 'general' | 'specific';

// Generate 24 hour time slots
const generateFullTimeSlots = (): ScheduleSlot[] => {
  return Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return {
      id: `${hour}:00`,
      time: `${hour}:00`,
      isAvailable: false,
      isBooked: false,
    };
  });
};

interface Props {
  initialData: BusinessProfileFormData;
  legalForms: LegalForm[];
  categories: TPayCategory[];
  onSave: (data: BusinessProfileFormData, facilityPlan?: File) => Promise<void>;
  onAvatarUpload: (file: File) => Promise<void>;
  isSaving: boolean;
  isNewProfile: boolean;
}

export default function BusinessProfileForm({
  initialData,
  legalForms,
  categories,
  onSave,
  onAvatarUpload,
  isSaving,
  isNewProfile,
}: Props) {
  const { t } = useTranslation();

  // Form state
  const [formData, setFormData] = useState<BusinessProfileFormData>(initialData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [facilityPlanFile, setFacilityPlanFile] = useState<File | null>(null);

  // Section collapse state
  const [sections, setSections] = useState({
    basicInfo: true,
    contactInfo: true,
    tpayIntegration: true,
    schedule: false,
    documentsMedia: false,
    trainerAssociations: false,
  });

  // Schedule state
  const [scheduleType, setScheduleType] = useState<ScheduleType>('general');
  const [selectedDay, setSelectedDay] = useState<DayType>('weekdays');
  const [specificDate, setSpecificDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Sync avatarUrl and facilityPlanUrl when they change from parent (after upload)
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      avatarUrl: initialData.avatarUrl,
      facilityPlanUrl: initialData.facilityPlanUrl,
      facilityPlanFileName: initialData.facilityPlanFileName,
    }));
  }, [initialData.avatarUrl, initialData.facilityPlanUrl, initialData.facilityPlanFileName]);

  const toggleSection = (section: keyof typeof sections) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFieldChange = useCallback((field: keyof BusinessProfileFormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validate field on change
    const newErrors = validateField(field, value, errors);
    setErrors(newErrors);
  }, [errors]);

  const handleFieldBlur = useCallback((field: keyof BusinessProfileFormData) => {
    const newErrors = validateField(field, formData[field], errors);
    setErrors(newErrors);
  }, [formData, errors]);

  const handleCitySelect = useCallback((city: City) => {
    // Update city name
    setFormData(prev => ({
      ...prev,
      city: city.Name,
      // Optionally auto-fill coordinates from city
      latitude: city.Latitude || prev.latitude,
      longitude: city.Longitude || prev.longitude,
    }));

    // Clear city error
    const newErrors = { ...errors };
    delete newErrors.city;
    setErrors(newErrors);
  }, [errors]);

  const handleCoordinatesChange = useCallback((coords: { latitude: number | null; longitude: number | null }) => {
    setFormData(prev => ({
      ...prev,
      latitude: coords.latitude ?? null,
      longitude: coords.longitude ?? null,
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate entire form
    const formErrors = validateForm(formData);
    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(formErrors)[0];
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    await onSave(formData, facilityPlanFile || undefined);
  };

  const handleFacilityPlanSelect = (file: File | null) => {
    setFacilityPlanFile(file);
  };

  const handleAvatarSelect = async (file: File | null) => {
    if (file) {
      await onAvatarUpload(file);
    }
  };

  // Schedule helpers
  const getScheduleKey = (day: DayType): 'weekdaysSchedule' | 'saturdaySchedule' | 'sundaySchedule' => {
    switch (day) {
      case 'weekdays': return 'weekdaysSchedule';
      case 'saturday': return 'saturdaySchedule';
      case 'sunday': return 'sundaySchedule';
    }
  };

  const getCurrentSlots = (): ScheduleSlot[] => {
    if (scheduleType === 'general') {
      const key = getScheduleKey(selectedDay);
      const slots = formData[key];
      return slots && slots.length > 0 ? slots : generateFullTimeSlots();
    } else {
      const dateSlots = formData.dateSpecificAvailability?.[specificDate];
      if (dateSlots) {
        return dateSlots.map(s => ({ id: s.time, time: s.time, isAvailable: s.isAvailable, isBooked: false }));
      }
      return generateFullTimeSlots();
    }
  };

  const toggleTimeSlot = (slotId: string) => {
    setFormData(prev => {
      const newFormData = { ...prev };

      if (scheduleType === 'general') {
        const key = getScheduleKey(selectedDay);
        const currentSlots = prev[key] && prev[key]!.length > 0 ? prev[key]! : generateFullTimeSlots();
        newFormData[key] = currentSlots.map(slot =>
          slot.time === slotId || slot.id === slotId
            ? { ...slot, isAvailable: !slot.isAvailable }
            : slot
        );
      } else {
        const dateSlots = prev.dateSpecificAvailability?.[specificDate] || generateFullTimeSlots().map(s => ({ time: s.time, isAvailable: s.isAvailable }));
        newFormData.dateSpecificAvailability = {
          ...prev.dateSpecificAvailability,
          [specificDate]: dateSlots.map(slot =>
            slot.time === slotId ? { ...slot, isAvailable: !slot.isAvailable } : slot
          ),
        };
      }

      return newFormData;
    });
  };

  const initializeSpecificDate = (date: string) => {
    if (!formData.dateSpecificAvailability?.[date]) {
      setFormData(prev => ({
        ...prev,
        dateSpecificAvailability: {
          ...prev.dateSpecificAvailability,
          [date]: generateFullTimeSlots().map(s => ({ time: s.time, isAvailable: false })),
        },
      }));
    }
  };

  // Use URLs directly - API returns full URLs
  const fullAvatarUrl = formData.avatarUrl || null;
  const fullFacilityPlanUrl = formData.facilityPlanUrl || null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information Section */}
      <FormSection
        title={t('businessProfile.sections.basicInfo')}
        isOpen={sections.basicInfo}
        onToggle={() => toggleSection('basicInfo')}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            id="companyName"
            label={t('businessProfile.fields.companyName')}
            value={formData.companyName}
            onChange={(v) => handleFieldChange('companyName', v)}
            onBlur={() => handleFieldBlur('companyName')}
            error={errors.companyName}
            required
          />
          <FormInput
            id="displayName"
            label={t('businessProfile.fields.displayName')}
            value={formData.displayName}
            onChange={(v) => handleFieldChange('displayName', v)}
            onBlur={() => handleFieldBlur('displayName')}
            error={errors.displayName}
            required
          />
        </div>

        <FormInput
          id="address"
          label={t('businessProfile.fields.address')}
          value={formData.address}
          onChange={(v) => handleFieldChange('address', v)}
          onBlur={() => handleFieldBlur('address')}
          error={errors.address}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CityAutocomplete
            value={formData.city}
            onChange={(v) => handleFieldChange('city', v)}
            onCitySelect={handleCitySelect}
            label={t('businessProfile.fields.city')}
            error={errors.city}
            required
          />
          <FormInput
            id="postalCode"
            label={t('businessProfile.fields.postalCode')}
            value={formData.postalCode}
            onChange={(v) => handleFieldChange('postalCode', v)}
            onBlur={() => handleFieldBlur('postalCode')}
            error={errors.postalCode}
            placeholder="00-000"
            required
          />
        </div>

        {/* Coordinates with Map Selector */}
        <CoordinateSelector
          latitude={formData.latitude ?? null}
          longitude={formData.longitude ?? null}
          onChange={handleCoordinatesChange}
          label={t('businessProfile.hints.coordinates')}
          hint={t('businessProfile.hints.coordinates')}
        />
      </FormSection>

      {/* Contact Information Section */}
      <FormSection
        title={t('businessProfile.sections.contactInfo')}
        isOpen={sections.contactInfo}
        onToggle={() => toggleSection('contactInfo')}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            id="email"
            label={t('businessProfile.fields.email')}
            type="email"
            value={formData.email}
            onChange={(v) => handleFieldChange('email', v)}
            onBlur={() => handleFieldBlur('email')}
            error={errors.email}
            required
          />
          <FormInput
            id="phoneNumber"
            label={t('businessProfile.fields.phoneNumber')}
            type="tel"
            value={formData.phoneNumber}
            onChange={(v) => handleFieldChange('phoneNumber', v)}
            onBlur={() => handleFieldBlur('phoneNumber')}
            error={errors.phoneNumber}
            placeholder="+48123456789"
            required
          />
        </div>
      </FormSection>

      {/* TPay Integration Section */}
      <FormSection
        title={t('businessProfile.sections.tpayIntegration')}
        isOpen={sections.tpayIntegration}
        onToggle={() => toggleSection('tpayIntegration')}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormInput
            id="nip"
            label={t('businessProfile.fields.nip')}
            value={formData.nip}
            onChange={(v) => handleFieldChange('nip', v)}
            onBlur={() => handleFieldBlur('nip')}
            error={errors.nip}
            placeholder="1234567890"
            maxLength={10}
            required
          />
          <FormInput
            id="regon"
            label={t('businessProfile.fields.regon')}
            value={formData.regon}
            onChange={(v) => handleFieldChange('regon', v)}
            onBlur={() => handleFieldBlur('regon')}
            error={errors.regon}
            placeholder="123456789"
          />
          <FormInput
            id="krs"
            label={t('businessProfile.fields.krs')}
            value={formData.krs}
            onChange={(v) => handleFieldChange('krs', v)}
            onBlur={() => handleFieldBlur('krs')}
            error={errors.krs}
            placeholder="0000000000"
            maxLength={10}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            id="legalForm"
            label={t('businessProfile.fields.legalForm')}
            value={formData.legalForm}
            onChange={(v) => handleFieldChange('legalForm', Number(v))}
            onBlur={() => handleFieldBlur('legalForm')}
            error={errors.legalForm}
            options={legalForms.map(lf => ({ value: lf.id, label: lf.name }))}
            placeholder={t('businessProfile.placeholders.selectLegalForm')}
            required
          />
          <FormSelect
            id="categoryId"
            label={t('businessProfile.fields.category')}
            value={formData.categoryId}
            onChange={(v) => handleFieldChange('categoryId', Number(v))}
            onBlur={() => handleFieldBlur('categoryId')}
            error={errors.categoryId}
            options={categories.map(c => ({ value: c.id, label: c.name }))}
            placeholder={t('businessProfile.placeholders.selectCategory')}
            required
          />
        </div>

        <FormInput
          id="website"
          label={t('businessProfile.fields.website')}
          type="url"
          value={formData.website}
          onChange={(v) => handleFieldChange('website', v)}
          onBlur={() => handleFieldBlur('website')}
          error={errors.website}
          placeholder="https://www.example.com"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            id="contactPersonName"
            label={t('businessProfile.fields.contactPersonName')}
            value={formData.contactPersonName}
            onChange={(v) => handleFieldChange('contactPersonName', v)}
            onBlur={() => handleFieldBlur('contactPersonName')}
            error={errors.contactPersonName}
            required
          />
          <FormInput
            id="contactPersonSurname"
            label={t('businessProfile.fields.contactPersonSurname')}
            value={formData.contactPersonSurname}
            onChange={(v) => handleFieldChange('contactPersonSurname', v)}
            onBlur={() => handleFieldBlur('contactPersonSurname')}
            error={errors.contactPersonSurname}
            required
          />
        </div>
      </FormSection>

      {/* Schedule Section */}
      <FormSection
        title={t('businessSchedule.title')}
        isOpen={sections.schedule}
        onToggle={() => toggleSection('schedule')}
      >
        <div className="space-y-4">
          {/* Schedule Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-ink-primary mb-2">
              {t('businessSchedule.scheduleType')}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setScheduleType('general')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  scheduleType === 'general'
                    ? 'bg-brand-primary text-white'
                    : 'bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover'
                }`}
              >
                {t('businessSchedule.generalSchedule')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setScheduleType('specific');
                  initializeSpecificDate(specificDate);
                }}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  scheduleType === 'specific'
                    ? 'bg-brand-primary text-white'
                    : 'bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover'
                }`}
              >
                {t('businessSchedule.specificDates')}
              </button>
            </div>
          </div>

          {/* Day Selection (for general) */}
          {scheduleType === 'general' && (
            <div>
              <label className="block text-sm font-medium text-ink-primary mb-2">
                {t('businessSchedule.selectDay')}
              </label>
              <div className="flex gap-2">
                {(['weekdays', 'saturday', 'sunday'] as DayType[]).map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDay(day)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      selectedDay === day
                        ? 'bg-brand-confirm text-white'
                        : 'bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover'
                    }`}
                  >
                    {t(`businessSchedule.${day}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date Picker (for specific) */}
          {scheduleType === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-ink-primary mb-2">
                {t('businessSchedule.selectDate')}
              </label>
              <input
                type="date"
                value={specificDate}
                onChange={e => {
                  setSpecificDate(e.target.value);
                  initializeSpecificDate(e.target.value);
                }}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
          )}

          {/* Legend */}
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-brand-confirm/30 border border-brand-confirm" />
              <span className="text-ink-secondary">{t('businessSchedule.available')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-surface-app-elevated border border-border-strong" />
              <span className="text-ink-secondary">{t('businessSchedule.unavailable')}</span>
            </div>
          </div>

          {/* Time Slots Grid */}
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-2">
            {getCurrentSlots().map(slot => (
              <button
                key={slot.id || slot.time}
                type="button"
                onClick={() => toggleTimeSlot(slot.time)}
                disabled={slot.isBooked}
                className={`py-2 px-1 rounded text-xs font-medium transition-colors ${
                  slot.isBooked
                    ? 'bg-brand-warm/30 text-brand-warm-deep dark:text-brand-warm cursor-not-allowed'
                    : slot.isAvailable
                    ? 'bg-brand-confirm/30 text-brand-confirm-deep dark:text-brand-confirm border border-brand-confirm hover:bg-brand-confirm/100/50'
                    : 'bg-surface-card text-ink-secondary border border-border-strong hover:bg-surface-card-hover'
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>

          {/* Info box */}
          <div className="flex items-start gap-2 p-3 bg-brand-primary/10 border border-brand-primary/30 rounded-lg">
            <svg className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-brand-primary">
              {scheduleType === 'general'
                ? t(`businessSchedule.infoGeneral.${selectedDay}`)
                : t('businessSchedule.infoSpecific')}
            </p>
          </div>
        </div>
      </FormSection>

      {/* Documents & Media Section */}
      <FormSection
        title={t('businessProfile.sections.documentsMedia')}
        isOpen={sections.documentsMedia}
        onToggle={() => toggleSection('documentsMedia')}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Avatar Upload */}
          <AvatarUpload
            label={t('businessProfile.fields.avatar')}
            currentAvatarUrl={fullAvatarUrl}
            onFileSelect={handleAvatarSelect}
            disabled={isNewProfile}
            hint={isNewProfile ? t('businessProfile.hints.saveFirst') : undefined}
            size="large"
            shape="square"
          />

          {/* Facility Plan Upload */}
          <FileUpload
            label={t('businessProfile.fields.facilityPlan')}
            accept=".pdf,.jpg,.jpeg,.png"
            maxSizeMB={10}
            currentFileUrl={fullFacilityPlanUrl}
            currentFileName={formData.facilityPlanFileName}
            onFileSelect={handleFacilityPlanSelect}
            hint={t('businessProfile.hints.facilityPlanFormats')}
          />
        </div>

        <FormInput
          id="termsAndConditionsUrl"
          label={t('businessProfile.fields.termsUrl')}
          type="url"
          value={formData.termsAndConditionsUrl || ''}
          onChange={(v) => handleFieldChange('termsAndConditionsUrl', v)}
          placeholder="https://www.example.com/terms"
        />

        <FormInput
          id="websiteDescription"
          label={t('businessProfile.fields.additionalDescription')}
          value={formData.websiteDescription || ''}
          onChange={(v) => handleFieldChange('websiteDescription', v)}
          placeholder={t('businessProfile.fields.additionalDescription')}
          multiline
          rows={4}
        />
      </FormSection>

      <FormSection
        title={t('businessProfile.sections.trainerAssociations')}
        isOpen={sections.trainerAssociations}
        onToggle={() => toggleSection('trainerAssociations')}
      >
        <div className="flex items-center justify-between gap-4 bg-surface-app rounded-lg p-4 border border-border-subtle">
          <div className="flex-1">
            <p className="text-sm font-semibold text-ink-primary mb-1">
              {t('businessProfile.fields.autoConfirmTrainerAssociations')}
            </p>
            <p className="text-sm text-ink-secondary">
              {t('businessProfile.hints.autoConfirmTrainerAssociations')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!formData.autoConfirmTrainerAssociations}
            onClick={() =>
              setFormData(prev => ({
                ...prev,
                autoConfirmTrainerAssociations: !prev.autoConfirmTrainerAssociations,
              }))
            }
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              formData.autoConfirmTrainerAssociations ? 'bg-brand-primary' : 'bg-border-strong'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.autoConfirmTrainerAssociations ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </FormSection>

      {/* Form Actions */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 border-t border-white/10">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-white/10 border border-white/30 text-white
                     hover:bg-white/20 transition-colors rounded-lg"
        >
          {t('businessProfile.actions.cancel')}
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-brand-primary text-white rounded-lg
                     hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-hidden="true" />
              {t('businessProfile.actions.saving')}
            </>
          ) : (
            t('businessProfile.actions.save')
          )}
        </button>
      </div>
    </form>
  );
}
